import { useEffect, useMemo, useState } from "react"

import { Col, Flex, Row, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import DropdownBlockCheckbox from "../../../Components/Filters/DropdownBlockCheckbox"
import DropdownBlockNumber from "../../../Components/Filters/DropdownBlockNumber"
import DropdownBlockTags from "../../../Components/Filters/DropdownBlockTags"
import Table from "../../../Components/Table"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import {
  ACTIVITY_CHANGE_DATA,
  buildColumns,
  REASON_DATA,
  STATUS_DATA
} from "../components/activityColumns"
import AnalyticsFilters from "../components/AnalyticsFilters"
import { useAnalyticsScope } from "../hooks/useAnalyticsScope"
import { usePageOf } from "../hooks/usePageOf"
import { ANALYTICS_SECTION } from "../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../redux/analytics.slice"

import styles from "../analytics.module.less"
import tables from "../tables.module.less"

const { Title } = Typography

const PAGE_SIZE = 50

// Empty array <-> "all" for the checkbox dropdowns (matches /recover/all).
const dropdownValue = (arr) => (!arr || arr.length === 0 ? "all" : arr)
const toArray = (v) => (v === "all" || v == null ? [] : Array.isArray(v) ? v : [v])
const csv = (list) => (list?.length ? list.join(",") : null)

// activity/'s own filters as query params, the empty ones left out.
const filterParams = ({ status, change, reason_category, customer, amount }) => {
  const hasAmount = amount?.type && (amount.min != null || amount.max != null)
  const params = {
    status: csv(status),
    change: csv(change),
    reason_category: csv(reason_category),
    customer: csv(customer),
    amount_type: hasAmount ? amount.type : null,
    amount_min: hasAmount ? amount.min : null,
    amount_max: hasAmount ? amount.max : null
  }
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value != null))
}

// Full "Activity stream" page (opened from the analytics card's "View all"). Shows
// every event in a table, with per-column filters. The Status / Category / Customer
// / Amount filters reuse the EXACT /recover/all filter components + semantics; the
// calendar and Type are the Analytics page's own, shared with it. activity/ filters
// and pages on the server.
const ActivityFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getBase } = useAnalyticsScope()
  const activity = useSelector(selectSectionData(ANALYTICS_SECTION.ACTIVITY_PAGE))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.ACTIVITY_PAGE))

  const [filters, setFilters] = useState({
    status: [],
    change: [],
    reason_category: [],
    customer: [],
    amount: null
  })
  const setF = (patch) => setFilters((prev) => ({ ...prev, ...patch }))

  // `getBase` changes with the window and the case type, which make a new query too: it is
  // what sends the table back to page 1 when the filters above move.
  const query = useMemo(
    () => filterParams(filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, getBase]
  )
  const [page, setPage] = usePageOf(query)

  useEffect(() => {
    getBase(ANALYTICS_SECTION.ACTIVITY_PAGE, { ...query, page, page_size: PAGE_SIZE })
  }, [getBase, query, page])

  const columns = useMemo(() => buildColumns(t), [t])

  // Same as the card: a row opens the dispute the event belongs to.
  const openDispute = (row) => ({
    onClick: () => navigate(RoutesPaths.recover.details(row.dispute_id)),
    style: { cursor: "pointer" }
  })

  useEffect(() => {
    document.title = "Activity stream - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <div className={styles.page}>
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Button
            type="text"
            onClick={() => navigate(RoutesPaths.recover.analytics)}
            icon={<Icon type="chevron_left_small" />}
            style={{ marginLeft: -12 }}
            text={t("Back to Analytics")}
          />
        </Col>

        <Col span={24}>
          <Title level={4} style={{ marginBottom: 0 }}>
            {t("Activity stream")}
          </Title>
        </Col>

        <Col span={24}>
          <Flex justify="space-between" align="center" gap={12} wrap="wrap">
            <Flex
              gap={12}
              align="center"
              wrap="wrap"
              className={cls("alert-filters", styles.subFilters)}
            >
              <DropdownBlockCheckbox
                id="dispute_state"
                label={t("Status")}
                allBtnText={t("All statuses")}
                data={STATUS_DATA}
                dropdownFilters={dropdownValue(filters.status)}
                setDropdownFilters={(obj) => setF({ status: toArray(obj.dispute_state) })}
              />
              <DropdownBlockCheckbox
                id="change"
                label={t("What changed")}
                iconType="pulse"
                allBtnText={t("All changes")}
                data={ACTIVITY_CHANGE_DATA}
                sortDisabled
                dropdownFilters={dropdownValue(filters.change)}
                setDropdownFilters={(obj) => setF({ change: toArray(obj.change) })}
              />
              <DropdownBlockCheckbox
                id="reason_category"
                label={t("Category")}
                allBtnText={t("All categories")}
                data={REASON_DATA}
                dropdownFilters={dropdownValue(filters.reason_category)}
                setDropdownFilters={(obj) =>
                  setF({ reason_category: toArray(obj.reason_category) })
                }
              />
              <DropdownBlockTags
                id="customer"
                label={t("Customer")}
                iconType="user"
                searchPlaceholder={t("Separate by comma")}
                dropdownFilters={filters.customer || []}
                setDropdownFilters={(obj) =>
                  setF({ customer: Array.isArray(obj.customer) ? obj.customer : [] })
                }
              />
              <DropdownBlockNumber
                id="amount"
                label={t("Amount")}
                iconType="money"
                searchPlaceholder=""
                min={0}
                dropdownFilters={filters.amount || {}}
                setDropdownFilters={(obj) => setF({ amount: obj.amount })}
              />
            </Flex>

            {/* Nothing in the feed is compared, so the shared calendar comes without Compare. */}
            <AnalyticsFilters withCompare={false} />
          </Flex>
        </Col>

        <Col span={24}>
          <div className={tables.rowHover}>
            <Table
              // Same table style as the other Recover inner pages (Chargeback Olympics
              // chrome). Columns unchanged.
              className={tables.olympics}
              dataSource={activity?.results ?? []}
              columns={columns}
              rowKey="event_id"
              loading={fetching}
              scroll={{}}
              onRow={openDispute}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: activity?.count ?? 0,
                showSizeChanger: false
              }}
              onChange={(pagination) => setPage(pagination.current)}
              EmptyComponent={() => <EmptyComponent type="no_data_available" />}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default ActivityFullPage
